import type { DeviceModel, PoEInSpec, PoEOutSpec, PoEStandard, PowerInput } from '../../types/deviceModel';
import { derivedPsuOutput, inputCapacityLimit, selfDrawLimitOf, selfDrawWattsAtVoltage } from '../../types/deviceModel';
import { collectModelPorts, layoutFromFacedPorts, type FacedPort } from './layoutPorts';
import { synthesizePortRoles } from './portRoles';

export function defaultPoeIn(): PoEInSpec {
  return { standards: ['802.3af'], voltageMin: 44, voltageMax: 57, maxWatts: 15 };
}

export function defaultPoeOut(produceable: PoEStandard[]): PoEOutSpec {
  return {
    standards: produceable.length ? produceable : ['802.3af'],
    perPortMaxWatts: 15.4,
    modeDefault: 'auto',
  };
}

export function pruneAndEnsurePoeInputs(nextPorts: FacedPort[], inputs: PowerInput[]): PowerInput[] {
  const kept = inputs.filter((i) => {
    if (i.kind !== 'poe-in') return true;
    return nextPorts.some((p) => p.poeIn && p.number === i.portNumber && (p.face || 'front') === (i.face || 'front'));
  });
  const withSpecs = kept.map((i) => {
    if (i.kind !== 'poe-in') return i;
    const p = nextPorts.find((x) => x.poeIn && x.number === i.portNumber && (x.face || 'front') === (i.face || 'front'));
    if (!p?.poeIn) return i;
    return { ...i, voltageMin: p.poeIn.voltageMin, voltageMax: p.poeIn.voltageMax, maxWatts: p.poeIn.maxWatts };
  });
  const extras: PowerInput[] = [];
  nextPorts.forEach((p) => {
    if (!p.poeIn) return;
    const has = withSpecs.some((i) => i.kind === 'poe-in' && i.portNumber === p.number && (i.face || 'front') === (p.face || 'front'));
    if (has) return;
    extras.push({
      id: `poe-in-${p.face}-${p.number}`,
      kind: 'poe-in',
      portNumber: p.number,
      face: p.face,
      voltageMin: p.poeIn.voltageMin,
      voltageMax: p.poeIn.voltageMax,
      maxWatts: p.poeIn.maxWatts,
    });
  });
  return [...withSpecs, ...extras];
}

export function initElectricalDraft(model: DeviceModel): DeviceModel {
  const cloned: DeviceModel = JSON.parse(JSON.stringify(model));
  const { roles, ports } = synthesizePortRoles(collectModelPorts(cloned), cloned.portRoles);
  return {
    ...cloned,
    selfDrawLimit: selfDrawLimitOf(cloned),
    portRoles: roles,
    layout: { ...cloned.layout, ...layoutFromFacedPorts(ports, cloned.layout) },
    electricalGraph: cloned.electricalGraph,
  };
}

export function finalizeDeviceModel(draft: DeviceModel): DeviceModel {
  const selfDrawLimit = selfDrawLimitOf(draft);
  const powerOutputs = (draft.powerOutputs || []).map((output) => {
    const capacityLimit = inputCapacityLimit(output);
    return capacityLimit === 'amps'
      ? { ...output, capacityLimit, maxWatts: undefined }
      : { ...output, capacityLimit, maxAmps: undefined };
  });
  const next: DeviceModel = {
    ...draft,
    selfDrawLimit,
    selfDrawAmps: selfDrawLimit === 'amps' ? draft.selfDrawAmps ?? 0 : undefined,
    selfDrawWatts:
      selfDrawLimit === 'watts'
        ? draft.selfDrawWatts
        : selfDrawWattsAtVoltage({ ...draft, selfDrawLimit: 'amps' }),
    powerInputs: draft.powerInputs.map((input) => {
      const capacityLimit = inputCapacityLimit(input);
      return capacityLimit === 'amps'
        ? { ...input, capacityLimit, maxWatts: undefined }
        : { ...input, capacityLimit, maxAmps: undefined };
    }),
    powerOutputs,
  };
  if (next.isPsu) {
    const derived = derivedPsuOutput(next);
    if (derived) next.psuOutput = derived;
  }
  if (next.isInverter) {
    next.requiresBatteryToStart = next.requiresBatteryToStart !== false;
  } else {
    delete next.requiresBatteryToStart;
  }
  return next;
}

export function mergePortsIntoDraft(
  draft: DeviceModel,
  nextPorts: FacedPort[],
  extra?: Partial<DeviceModel>
): { draft: DeviceModel; ports: FacedPort[] } {
  const seedRoles = extra?.portRoles ?? draft.portRoles;
  const { roles, ports: stamped } = synthesizePortRoles(nextPorts, seedRoles);
  const powerInputs = extra?.powerInputs ?? pruneAndEnsurePoeInputs(stamped, draft.powerInputs);
  const restExtra = extra ? { ...extra } : {};
  delete restExtra.portRoles;
  delete restExtra.powerInputs;
  return {
    ports: stamped,
    draft: {
      ...draft,
      ...restExtra,
      portRoles: roles,
      powerInputs,
      layout: { ...draft.layout, ...layoutFromFacedPorts(stamped, draft.layout) },
      electricalGraph: extra?.electricalGraph ?? draft.electricalGraph,
    },
  };
}
